<template>
  <div>
    <section class="section">
      <div class="container">
        <div>
          <div class="text-right">
            <b-button
              v-if="hasUnsavedConfiguration"
              class="mr-4"
              type="is-info"
              tag="router-link"
              to="/uni-baies/resume"
            >
              <strong>
                Configuration en cours
              </strong>
            </b-button>
            <b-button
              type="is-primary"
              tag="router-link"
              to="/uni-baies/configuration"
            >
              <strong>
                Nouvelle configuration
              </strong>
            </b-button>
          </div>
          <BaseTitle>
            Liste des configurations
          </BaseTitle>
        </div>

        <div v-if="connected && configurations.length">
          <div
            v-for="configuration in configurations"
            :key="configuration.id"
            class="card mb-6"
          >
            <div class="card-header">
              <div class="card-header-title">
                {{ configuration.name }}
              </div>
            </div>
            <div class="card-content">
              <div class="content flex justify-between items-end">
                <div>
                  <time
                    :datetime="configuration.created_at"
                  >Le {{ configuration.created_at | moment('LL') }}</time>
                  <br><br>
                  {{ configuration.comment }}
                </div>
                <div>
                  <b-button
                    tag="router-link"
                    :to="`/uni-baies/configuration/${configuration.id}/resume`"
                    type="is-link"
                    class="mr-3 is-primary"
                  >
                    Consulter
                  </b-button>
                  <b-button
                    type="is-primary"
                    outlined
                    class="mr-3"
                    :to="`/uni-baies/configuration/${configuration.id}`"
                    tag="router-link"
                  >
                    Modifier
                  </b-button>
                  <b-button
                    type="is-primary"
                    outlined
                    class="mr-3"
                    @click="openDuplicateModal(configuration)"
                  >
                    Dupliquer
                  </b-button>
                  <b-button
                    type="is-danger"
                    @click="confirmDelete(configuration)"
                  >
                    Supprimer
                  </b-button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <template v-else>
          <div class="flex justify-center mb-6">
            <EmptySvg class="placeholder-img" />
          </div>
          <div
            class="flex justify-center"
          >
            <div>
              <div class="mb-4">
                Vous n'avez pas encore de configuration
              </div>
              <b-button
                class="mb-4"
                type="is-primary"
                expanded
                tag="router-link"
                to="/uni-baies/configuration"
              >
                <strong>
                  Créer une configuration
                </strong>
              </b-button>
              <b-button
                v-if="!connected"
                tag="router-link"
                type="is-light"
                :to="`/connexion?redirect=${$router.currentRoute.path}`"
                class="mb-4"
                expanded
              >
                Me connecter
              </b-button>
              <b-button
                v-if="!connected"
                tag="router-link"
                type="is-text"
                to="/inscription"
                expanded
              >
                Créer un compte
              </b-button>
            </div>
          </div>
        </template>
      </div>
    </section>
    <b-modal
      :active.sync="isDuplicateModalActive"
      has-modal-card
      trap-focus
      aria-role="dialog"
      aria-modal
    >
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Dupliquer une configuration
          </p>
        </header>
        <form
          action=""
          @submit.prevent="performDuplication"
        >
          <section class="modal-card-body">
            <b-field label="Nom">
              <b-input
                v-model="duplicatedConfiguration.name"
                type="text"
                placeholder="Nom de la configuration"
                required
              />
            </b-field>
            <b-field
              label="Commentaire"
            >
              <b-input
                v-model="duplicatedConfiguration.comment"
                type="textarea"
                required
              />
            </b-field>
          </section>
          <footer class="modal-card-foot justify-end">
            <b-button
              type="is-primary"
              outlined
              @click.prevent="closeModal"
            >
              Annuler
            </b-button>
            <b-button
              type="is-primary"
              native-type="submit"
            >
              Confirmer
            </b-button>
          </footer>
        </form>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import BaseConfirmModal from '../components/BaseConfirmModal.vue';
import BaseTitle from '../components/BaseTitle.vue';
import EmptySvg from '../assets/undraw_empty.svg';

export default {
  name: 'ConfigurationsList',
  components: {
    BaseTitle,
    EmptySvg,
  },
  beforeRouteLeave(to, from, next) {
    if (!this.hasUnsavedConfiguration) {
      this.clearCurrentConfiguration();
      next();
    }
    if (this.hasUnsavedConfiguration
      && to.path !== '/uni-baies/resume'
      && to.path.split('?')[0] !== '/connexion'
      && to.path !== '/inscription'
      && to.path !== '/uni-trunk/configuration'
      && parseInt(to.params.id, 10) !== this.currentConfiguration.id) {
      this.$buefy.modal.open({
        parent: this,
        component: BaseConfirmModal,
        hasModalCard: true,
        trapFocus: true,
        props: {
          dangerButtonText: 'Écraser la configuration actuelle',
          message: 'Vous avez une configuration en cours. Êtes-vous sur de vouloir continuer ?',
        },
        events: {
          confirm: () => {
            this.clearCurrentConfiguration().then(() => {
              next();
            });
          },
        },
      });
    } else {
      next();
    }
  },
  data() {
    return {
      configurationToDuplicate: {},
      duplicatedConfiguration: {},
      isDuplicateModalActive: false,
    };
  },
  computed: {
    ...mapGetters('authentication', ['connected']),
    ...mapState('configuration', ['configurations', 'currentConfiguration']),
    hasUnsavedConfiguration() {
      return this.currentConfiguration.configuration_products.length
          && this.currentConfiguration.unsaved;
    },
  },
  created() {
    this.fetchConfigurations();
  },
  methods: {
    ...mapActions('authentication', ['fetchMe', 'signOut']),
    ...mapActions('configuration', ['clearCurrentConfiguration', 'fetchConfigurations',
      'duplicateConfiguration', 'deleteConfiguration']),
    confirmDelete(configuration) {
      this.$buefy.modal.open({
        parent: this,
        component: BaseConfirmModal,
        hasModalCard: true,
        trapFocus: true,
        props: {
          message: `Êtes-vous sur de vouloir supprimer la configuration ${configuration.name}`,
        },
        events: {
          confirm: () => {
            this.deleteConfiguration(configuration.id);
          },
        },
      });
    },
    openDuplicateModal(configuration) {
      this.configurationToDuplicate = configuration;
      this.duplicatedConfiguration = {
        name: configuration.name,
        comment: configuration.comment,
      };
      this.isDuplicateModalActive = true;
    },
    closeModal() {
      this.isDuplicateModalActive = false;
    },
    performDuplication() {
      this.duplicateConfiguration({
        configurationId: this.configurationToDuplicate.id,
        configuration: this.duplicatedConfiguration,
      });
      this.closeModal();
    },
  },
};
</script>

<style lang="scss" scoped>
.placeholder-img {
  width: 300px;
  height: auto;
}
</style>
